import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Handle,
  Position,
  MarkerType,
  Controls,
  Background
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './react-flow.scss';
import { getHsvHoldingStructure } from '../hsvCorpData';
import { normalizeLang } from '../../../../utils/localeDataLoader';

// --- CUSTOM NODES WITH FULL HANDLES ---

const CeoNode = ({ data }) => (
  <div className="rf-org-ceo-node">
    <Handle type="target" position={Position.Top} id="top" />
    <div className="node-title">{data.label}</div>
    <Handle type="source" position={Position.Bottom} id="bottom" />
  </div>
);

const ExecutiveNode = ({ data }) => (
  <div className={`rf-org-exec-node ${data.variant || 'blue'}`}>
    <Handle type="target" position={Position.Top} id="top" />
    <Handle type="target" position={Position.Left} id="left" />
    <Handle type="source" position={Position.Right} id="right" />
    <Handle type="source" position={Position.Bottom} id="bottom" />
    <div className="node-title">{data.label}</div>
  </div>
);

const DirectorNode = ({ data }) => (
  <div className="rf-org-director-node">
    <Handle type="target" position={Position.Top} id="top" />
    <Handle type="target" position={Position.Left} id="left" />
    <Handle type="source" position={Position.Right} id="right" />
    <Handle type="source" position={Position.Bottom} id="bottom" />
    <div className="node-title">{data.label}</div>
  </div>
);

const DepartmentCardNode = ({ data }) => (
  <div className="rf-org-dept-card">
    <Handle type="target" position={Position.Top} id="top" />
    <div className="dept-card-header">{data.name}</div>
    {data.tasks && data.tasks.length > 0 && (
      <div className="dept-card-tasks">
        {data.tasks.map((task, idx) => (
          <div key={idx} className="task-item">
            <span className="task-bullet">•</span> {task}
          </div>
        ))}
      </div>
    )}
    <Handle type="source" position={Position.Bottom} id="bottom" />
  </div>
);

const UnitNode = ({ data }) => (
  <div className="rf-org-unit-node">
    <Handle type="target" position={Position.Top} id="top" />
    <Handle type="target" position={Position.Right} id="right" />
    <div className="node-title">{data.label}</div>
  </div>
);

const nodeTypes = {
  ceoNode: CeoNode,
  execNode: ExecutiveNode,
  directorNode: DirectorNode,
  deptCardNode: DepartmentCardNode,
  unitNode: UnitNode,
};

const defaultEdgeStyle = {
  stroke: '#4db748',
  strokeWidth: 2,
};

const defaultMarker = {
  type: MarkerType.ArrowClosed,
  color: '#4db748',
  width: 14,
  height: 14,
};

export default function HsvOrgChartReactFlow({ isEnglish }) {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const isEng = isEnglish !== undefined ? isEnglish : (lang === 'en');
  const holdingStruct = getHsvHoldingStructure(isEng);
  const orgData = holdingStruct?.orgChart;

  const generatedNodes = useMemo(() => {
    if (!orgData) return [];

    return [
      // Level 0: CEO
      {
        id: 'node-ceo',
        type: 'ceoNode',
        position: { x: 850, y: 15 },
        data: { label: orgData.ceo || 'CEO' },
      },

      // Level 1: Executive / Advisory Nodes + Kế toán + Kiểm toán
      {
        id: 'node-exec-0',
        type: 'execNode',
        position: { x: 50, y: 160 },
        data: { label: orgData.topStaff?.[0] || 'Hợp tác quốc tế & truyền thông', variant: 'blue' },
      },
      {
        id: 'node-dept-1', // Kế toán
        type: 'execNode',
        position: { x: 450, y: 160 },
        data: { label: orgData.departments?.[1]?.name || 'Kế toán', variant: 'blue' },
      },
      {
        id: 'node-exec-1',
        type: 'execNode',
        position: { x: 850, y: 160 },
        data: { label: orgData.topStaff?.[1] || 'Ban chiến lược', variant: 'blue' },
      },
      {
        id: 'node-dept-5', // Kiểm toán nội bộ
        type: 'execNode',
        position: { x: 1250, y: 160 },
        data: { label: orgData.departments?.[5]?.name || 'Kiểm toán nội bộ & quản lý rủi ro', variant: 'blue' },
      },
      {
        id: 'node-exec-2',
        type: 'execNode',
        position: { x: 1650, y: 160 },
        data: { label: orgData.topStaff?.[2] || 'Quỹ trách nhiệm xã hội', variant: 'green' },
      },

      // Level 2: Directors (under Ban chiến lược)
      {
        id: 'node-dir-0',
        type: 'directorNode',
        position: { x: 610, y: 300 },
        data: { label: orgData.directors?.[0] || 'GĐ Đầu tư' },
      },
      {
        id: 'node-dir-1',
        type: 'directorNode',
        position: { x: 850, y: 300 },
        data: { label: orgData.directors?.[1] || 'GĐ Kinh doanh' },
      },
      {
        id: 'node-dir-2',
        type: 'directorNode',
        position: { x: 1090, y: 300 },
        data: { label: orgData.directors?.[2] || 'GĐ Chuyên môn' },
      },

      // Level 2.5: Medical Center (Under GĐ Chuyên môn)
      {
        id: 'node-med-center',
        type: 'directorNode',
        position: { x: 1090, y: 420 },
        data: { label: orgData.medicalCenter || 'Trung tâm y khoa kỹ thuật cao' },
      },

      // Level 3/4: 4 other departments (under Quỹ trách nhiệm xã hội)
      ...([0, 2, 3, 4].map((deptIndex, i) => {
        const dept = orgData.departments?.[deptIndex];
        const xPositions = [1320, 1540, 1760, 1980]; 
        const yPositions = [300, 300, 300, 300];
        return {
          id: `node-dept-${deptIndex}`,
          type: 'deptCardNode',
          position: { x: xPositions[i], y: yPositions[i] },
          data: { name: dept?.name || '', tasks: dept?.tasks || [] },
        };
      })),

      // Level 5: 6 Member Unit Nodes
      ...(orgData.units || []).map((unit, i) => {
        // Center the 6 units under med-center (x=1090) with 240px gap
        const xPositions = [490, 730, 970, 1210, 1450, 1690];
        return {
          id: `node-unit-${i}`,
          type: 'unitNode',
          position: { x: xPositions[i], y: 620 },
          data: { label: unit },
        };
      }),
    ];
  }, [orgData]);

  const generatedEdges = useMemo(() => {
    if (!orgData) return [];

    const edges = [
      // 1. CEO -> Top Staff Nodes
      { id: 'e-ceo-exec0', source: 'node-ceo', target: 'node-exec-0', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-ceo-exec1', source: 'node-ceo', target: 'node-exec-1', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-ceo-exec2', source: 'node-ceo', target: 'node-exec-2', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },

      // 2. Ban chiến lược -> 3 Directors
      { id: 'e-exec1-dir0', source: 'node-exec-1', target: 'node-dir-0', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-exec1-dir1', source: 'node-exec-1', target: 'node-dir-1', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-exec1-dir2', source: 'node-exec-1', target: 'node-dir-2', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },

      // 3. GĐ Chuyên môn -> Trung tâm y khoa KTC
      { id: 'e-dir2-med', source: 'node-dir-2', target: 'node-med-center', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },

      // 4. CEO -> Kế toán (1) & Kiểm toán nội bộ (5)
      { id: 'e-ceo-dept1', source: 'node-ceo', target: 'node-dept-1', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-ceo-dept5', source: 'node-ceo', target: 'node-dept-5', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },

      // 5. Quỹ TNXH -> Tài chính (0), Pháp lý (2), Quản trị công ty (3), Quản trị nhân lực (4)
      { id: 'e-csr-dept0', source: 'node-exec-2', target: 'node-dept-0', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-csr-dept2', source: 'node-exec-2', target: 'node-dept-2', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-csr-dept3', source: 'node-exec-2', target: 'node-dept-3', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },
      { id: 'e-csr-dept4', source: 'node-exec-2', target: 'node-dept-4', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: defaultEdgeStyle, markerEnd: defaultMarker },

      // 6. Connect ALL 6 Units to Trung tâm y khoa KTC
      { id: 'e-med-unit0', source: 'node-med-center', target: 'node-unit-0', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
      { id: 'e-med-unit1', source: 'node-med-center', target: 'node-unit-1', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
      { id: 'e-med-unit2', source: 'node-med-center', target: 'node-unit-2', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
      { id: 'e-med-unit3', source: 'node-med-center', target: 'node-unit-3', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
      { id: 'e-med-unit4', source: 'node-med-center', target: 'node-unit-4', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
      { id: 'e-med-unit5', source: 'node-med-center', target: 'node-unit-5', sourceHandle: 'bottom', targetHandle: 'top', type: 'smoothstep', style: { stroke: '#4db748', strokeWidth: 2 }, markerEnd: defaultMarker },
    ];

    return edges;
  }, [orgData]);

  return (
    <div className="hsv-flow-mobile-scroll-container">
      <div className="hsv-flow-scroll-hint">
        👈 {isEng ? 'Swipe/Drag to view chart' : 'Vuốt/Kéo để xem sơ đồ'} 👉
      </div>

      <div className="hsv-react-flow-wrapper hsv-org-chart-wrapper">
        <ReactFlow
          nodes={generatedNodes}
          edges={generatedEdges}
          nodeTypes={nodeTypes}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          zoomOnScroll={false}
          panOnScroll={false}
          panOnDrag={true}
          fitView
          fitViewOptions={{ padding: 0.1, minZoom: 1.2, maxZoom: 2 }}
        >
          <Controls showInteractive={false} />
          <Background color="#cbd5e1" gap={24} size={1} />
        </ReactFlow>
      </div>
    </div>
  );
}
